import { useQuery } from "@tanstack/react-query";
import {
  fetchDataSourceReports,
  fetchDataSourceTables,
  fetchDataSourceUsedIn,
  fetchNodeOverview,
  fetchOntologyLinks,
  fetchOntologyMetrics,
  fetchOntologyOverview,
  fetchOntologyReports,
  fetchOntologyRules,
  fetchOntologyUsedIn,
  fetchMetricFormula,
  fetchMetricOverview,
  fetchMetricUsedIn,
  fetchDataStoryActivity,
  fetchDataStoryOverview,
  fetchDataStoryTrace,
  fetchArtifactActivity,
  fetchArtifactOverview,
  fetchDataModelMetrics,
  fetchDataModelOverview,
  fetchDataModelRelationships,
  fetchDataModelTables,
  fetchReportActivity,
  fetchReportOverview,
  fetchReportTrace,
  fetchReportUsedIn,
  fetchSchemaMetrics,
  fetchSchemaRelationships,
  fetchSchemaUsedIn,
  fetchTableOverview,
} from "../../../../services/kg/kgDrawerApi";
import {
  mapMetricsResponse,
  mapOntologyLinksResponse,
  mapMetricFormulaToView,
  mapMetricOverviewToView,
  mapDataStoryActivityToView,
  mapDataStoryOverviewToView,
  mapArtifactActivityToView,
  mapArtifactOverviewToView,
  mapDataModelOverviewToView,
  mapDataModelRelationshipsResponse,
  mapReportActivityToView,
  mapReportOverviewToView,
  mapOntologyOverviewToView,
  mapTraceResponse,
  mapOntologyReportsResponse,
  mapOntologyRulesResponse,
  mapOverviewToView,
  mapRelationshipsResponse,
  mapReportsResponse,
  mapTableOverviewToView,
  mapTablesResponse,
  mapUsedInResponse,
} from "../../../../services/kg/kgDrawerMappers";
import { queryKeys } from "../../../../services/queryKeys";
import type { KgFlowNodeType } from "../types";
import type { InspectorTabId, KgInspectorTabData } from "../inspector/types";

export interface UseKgInspectorTabDataOptions {
  nodeId: string | null;
  nodeType: KgFlowNodeType | null;
  activeTab: string;
  enabled?: boolean;
}

const isDataSourceListTab = (
  tab: string,
): tab is "tables" | "reports" | "usedIn" =>
  tab === "tables" || tab === "reports" || tab === "usedIn";

const isTableListTab = (
  tab: string,
): tab is "relationship" | "metrics" | "usedIn" =>
  tab === "relationship" || tab === "metrics" || tab === "usedIn";

const isOntologyListTab = (
  tab: string,
): tab is "links" | "metrics" | "rules" | "reports" | "usedIn" =>
  tab === "links" ||
  tab === "metrics" ||
  tab === "rules" ||
  tab === "reports" ||
  tab === "usedIn";

const isMetricListTab = (tab: string): tab is "formula" | "usedIn" =>
  tab === "formula" || tab === "usedIn";

const isReportListTab = (
  tab: string,
): tab is "trace" | "usedIn" | "activity" =>
  tab === "trace" || tab === "usedIn" || tab === "activity";

const isDataStoryListTab = (tab: string): tab is "trace" | "activity" =>
  tab === "trace" || tab === "activity";

const isArtifactListTab = (tab: string): tab is "activity" =>
  tab === "activity";

export const useKgInspectorTabData = ({
  nodeId,
  nodeType,
  activeTab,
  enabled = true,
}: UseKgInspectorTabDataOptions): KgInspectorTabData => {
  const isDataSource = nodeType === "dataSource";
  const isTable = nodeType === "table";
  const isOntology = nodeType === "ontology";
  const isMetric = nodeType === "metric";
  const isReport = nodeType === "report";
  const isDataStory = nodeType === "dataStory";
  const isArtifact = nodeType === "artifact";
  const isDataModel = nodeType === "dataModel";
  const tabId = activeTab as InspectorTabId;
  const queryEnabled = Boolean(
    enabled &&
      nodeId &&
      (isDataSource ||
        isTable ||
        isOntology ||
        isMetric ||
        isReport ||
        isDataStory ||
        isArtifact ||
        isDataModel),
  );

  const dataSourceOverviewQuery = useQuery({
    queryKey: queryKeys.kg.overview(nodeId ?? ""),
    queryFn: () => fetchNodeOverview(nodeId!),
    enabled: queryEnabled && isDataSource && tabId === "overview",
    select: mapOverviewToView,
  });

  const tableOverviewQuery = useQuery({
    queryKey: queryKeys.kg.tableOverview(nodeId ?? ""),
    queryFn: () => fetchTableOverview(nodeId!),
    enabled: queryEnabled && isTable && tabId === "overview",
    select: mapTableOverviewToView,
  });

  const ontologyOverviewQuery = useQuery({
    queryKey: queryKeys.kg.ontologyOverview(nodeId ?? ""),
    queryFn: () => fetchOntologyOverview(nodeId!),
    enabled: queryEnabled && isOntology && tabId === "overview",
    select: mapOntologyOverviewToView,
  });

  const tablesQuery = useQuery({
    queryKey: queryKeys.kg.tables(nodeId ?? ""),
    queryFn: () => fetchDataSourceTables(nodeId!),
    enabled: queryEnabled && isDataSource && tabId === "tables",
    select: mapTablesResponse,
  });

  const reportsQuery = useQuery({
    queryKey: queryKeys.kg.reports(nodeId ?? ""),
    queryFn: () => fetchDataSourceReports(nodeId!),
    enabled: queryEnabled && isDataSource && tabId === "reports",
    select: mapReportsResponse,
  });

  const dataSourceUsedInQuery = useQuery({
    queryKey: queryKeys.kg.usedIn(nodeId ?? ""),
    queryFn: () => fetchDataSourceUsedIn(nodeId!),
    enabled: queryEnabled && isDataSource && tabId === "usedIn",
    select: mapUsedInResponse,
  });

  const relationshipsQuery = useQuery({
    queryKey: queryKeys.kg.schemaRelationships(nodeId ?? ""),
    queryFn: () => fetchSchemaRelationships(nodeId!),
    enabled: queryEnabled && isTable && tabId === "relationship",
    select: mapRelationshipsResponse,
  });

  const schemaMetricsQuery = useQuery({
    queryKey: queryKeys.kg.schemaMetrics(nodeId ?? ""),
    queryFn: () => fetchSchemaMetrics(nodeId!),
    enabled: queryEnabled && isTable && tabId === "metrics",
    select: mapMetricsResponse,
  });

  const schemaUsedInQuery = useQuery({
    queryKey: queryKeys.kg.schemaUsedIn(nodeId ?? ""),
    queryFn: () => fetchSchemaUsedIn(nodeId!),
    enabled: queryEnabled && isTable && tabId === "usedIn",
    select: mapUsedInResponse,
  });

  const ontologyLinksQuery = useQuery({
    queryKey: queryKeys.kg.ontologyLinks(nodeId ?? ""),
    queryFn: () => fetchOntologyLinks(nodeId!),
    enabled: queryEnabled && isOntology && tabId === "links",
    select: mapOntologyLinksResponse,
  });

  const ontologyMetricsQuery = useQuery({
    queryKey: queryKeys.kg.ontologyMetrics(nodeId ?? ""),
    queryFn: () => fetchOntologyMetrics(nodeId!),
    enabled: queryEnabled && isOntology && tabId === "metrics",
    select: mapMetricsResponse,
  });

  const ontologyRulesQuery = useQuery({
    queryKey: queryKeys.kg.ontologyRules(nodeId ?? ""),
    queryFn: () => fetchOntologyRules(nodeId!),
    enabled: queryEnabled && isOntology && tabId === "rules",
    select: mapOntologyRulesResponse,
  });

  const ontologyReportsQuery = useQuery({
    queryKey: queryKeys.kg.ontologyReports(nodeId ?? ""),
    queryFn: () => fetchOntologyReports(nodeId!),
    enabled: queryEnabled && isOntology && tabId === "reports",
    select: mapOntologyReportsResponse,
  });

  const ontologyUsedInQuery = useQuery({
    queryKey: queryKeys.kg.ontologyUsedIn(nodeId ?? ""),
    queryFn: () => fetchOntologyUsedIn(nodeId!),
    enabled: queryEnabled && isOntology && tabId === "usedIn",
    select: mapUsedInResponse,
  });

  const metricOverviewQuery = useQuery({
    queryKey: queryKeys.kg.metricOverview(nodeId ?? ""),
    queryFn: () => fetchMetricOverview(nodeId!),
    enabled: queryEnabled && isMetric,
    select: mapMetricOverviewToView,
  });

  const metricFormulaQuery = useQuery({
    queryKey: queryKeys.kg.metricFormula(nodeId ?? ""),
    queryFn: () => fetchMetricFormula(nodeId!),
    enabled: queryEnabled && isMetric && tabId === "formula",
    select: mapMetricFormulaToView,
  });

  const metricUsedInQuery = useQuery({
    queryKey: queryKeys.kg.metricUsedIn(nodeId ?? ""),
    queryFn: () => fetchMetricUsedIn(nodeId!),
    enabled: queryEnabled && isMetric && tabId === "usedIn",
    select: mapUsedInResponse,
  });

  const reportOverviewQuery = useQuery({
    queryKey: queryKeys.kg.reportOverview(nodeId ?? ""),
    queryFn: () => fetchReportOverview(nodeId!),
    enabled: queryEnabled && isReport,
    select: mapReportOverviewToView,
  });

  const reportTraceQuery = useQuery({
    queryKey: queryKeys.kg.reportTrace(nodeId ?? ""),
    queryFn: () => fetchReportTrace(nodeId!),
    enabled: queryEnabled && isReport && tabId === "trace",
    select: mapTraceResponse,
  });

  const reportUsedInQuery = useQuery({
    queryKey: queryKeys.kg.reportUsedIn(nodeId ?? ""),
    queryFn: () => fetchReportUsedIn(nodeId!),
    enabled: queryEnabled && isReport && tabId === "usedIn",
    select: mapUsedInResponse,
  });

  const reportActivityQuery = useQuery({
    queryKey: queryKeys.kg.reportActivity(nodeId ?? ""),
    queryFn: () => fetchReportActivity(nodeId!),
    enabled: queryEnabled && isReport && tabId === "activity",
    select: mapReportActivityToView,
  });

  const dataStoryOverviewQuery = useQuery({
    queryKey: queryKeys.kg.dataStoryOverview(nodeId ?? ""),
    queryFn: () => fetchDataStoryOverview(nodeId!),
    enabled: queryEnabled && isDataStory,
    select: mapDataStoryOverviewToView,
  });

  const dataStoryTraceQuery = useQuery({
    queryKey: queryKeys.kg.dataStoryTrace(nodeId ?? ""),
    queryFn: () => fetchDataStoryTrace(nodeId!),
    enabled: queryEnabled && isDataStory && tabId === "trace",
    select: mapTraceResponse,
  });

  const dataStoryActivityQuery = useQuery({
    queryKey: queryKeys.kg.dataStoryActivity(nodeId ?? ""),
    queryFn: () => fetchDataStoryActivity(nodeId!),
    enabled: queryEnabled && isDataStory && tabId === "activity",
    select: mapDataStoryActivityToView,
  });

  const artifactOverviewQuery = useQuery({
    queryKey: queryKeys.kg.artifactOverview(nodeId ?? ""),
    queryFn: () => fetchArtifactOverview(nodeId!),
    enabled: queryEnabled && isArtifact,
    select: mapArtifactOverviewToView,
  });

  const artifactActivityQuery = useQuery({
    queryKey: queryKeys.kg.artifactActivity(nodeId ?? ""),
    queryFn: () => fetchArtifactActivity(nodeId!),
    enabled: queryEnabled && isArtifact && tabId === "activity",
    select: mapArtifactActivityToView,
  });

  const dataModelOverviewQuery = useQuery({
    queryKey: queryKeys.kg.dataModelOverview(nodeId ?? ""),
    queryFn: () => fetchDataModelOverview(nodeId!),
    enabled: queryEnabled && isDataModel && tabId === "overview",
    select: mapDataModelOverviewToView,
  });

  const dataModelTablesQuery = useQuery({
    queryKey: queryKeys.kg.dataModelTables(nodeId ?? ""),
    queryFn: () => fetchDataModelTables(nodeId!),
    enabled: queryEnabled && isDataModel && tabId === "tables",
    select: mapTablesResponse,
  });

  const dataModelMetricsQuery = useQuery({
    queryKey: queryKeys.kg.dataModelMetrics(nodeId ?? ""),
    queryFn: () => fetchDataModelMetrics(nodeId!),
    enabled: queryEnabled && isDataModel && tabId === "metrics",
    select: mapMetricsResponse,
  });

  const dataModelRelationshipsQuery = useQuery({
    queryKey: queryKeys.kg.dataModelRelationships(nodeId ?? ""),
    queryFn: () => fetchDataModelRelationships(nodeId!),
    enabled: queryEnabled && isDataModel && tabId === "relationships",
    select: mapDataModelRelationshipsResponse,
  });

  if (
    !nodeId ||
    (!isDataSource &&
      !isTable &&
      !isOntology &&
      !isMetric &&
      !isReport &&
      !isDataStory &&
      !isArtifact &&
      !isDataModel)
  ) {
    return { isLoading: false, error: null };
  }

  if (isTable && !isTableListTab(tabId) && tabId !== "overview") {
    return { isLoading: false, error: null };
  }

  if (isDataSource && !isDataSourceListTab(tabId) && tabId !== "overview") {
    return { isLoading: false, error: null };
  }

  if (isOntology && !isOntologyListTab(tabId) && tabId !== "overview") {
    return { isLoading: false, error: null };
  }

  if (isMetric && !isMetricListTab(tabId) && tabId !== "overview") {
    return { isLoading: false, error: null };
  }

  if (isReport && !isReportListTab(tabId) && tabId !== "overview") {
    return { isLoading: false, error: null };
  }

  if (isDataStory && !isDataStoryListTab(tabId) && tabId !== "overview") {
    return { isLoading: false, error: null };
  }

  if (isArtifact && !isArtifactListTab(tabId) && tabId !== "overview") {
    return { isLoading: false, error: null };
  }

  switch (tabId) {
    case "overview": {
      if (isDataModel) {
        return {
          isLoading: dataModelOverviewQuery.isLoading,
          error: dataModelOverviewQuery.error
            ? String(dataModelOverviewQuery.error)
            : null,
          dataModelOverview: dataModelOverviewQuery.data,
        };
      }
      if (isArtifact) {
        return {
          isLoading: artifactOverviewQuery.isLoading,
          error: artifactOverviewQuery.error
            ? String(artifactOverviewQuery.error)
            : null,
          artifactOverview: artifactOverviewQuery.data,
        };
      }
      if (isDataStory) {
        return {
          isLoading: dataStoryOverviewQuery.isLoading,
          error: dataStoryOverviewQuery.error
            ? String(dataStoryOverviewQuery.error)
            : null,
          dataStoryOverview: dataStoryOverviewQuery.data,
        };
      }
      if (isReport) {
        return {
          isLoading: reportOverviewQuery.isLoading,
          error: reportOverviewQuery.error
            ? String(reportOverviewQuery.error)
            : null,
          reportOverview: reportOverviewQuery.data,
        };
      }
      if (isMetric) {
        return {
          isLoading: metricOverviewQuery.isLoading,
          error: metricOverviewQuery.error
            ? String(metricOverviewQuery.error)
            : null,
          metricOverview: metricOverviewQuery.data,
        };
      }
      if (isOntology) {
        return {
          isLoading: ontologyOverviewQuery.isLoading,
          error: ontologyOverviewQuery.error
            ? String(ontologyOverviewQuery.error)
            : null,
          ontologyOverview: ontologyOverviewQuery.data,
        };
      }
      if (isTable) {
        return {
          isLoading: tableOverviewQuery.isLoading,
          error: tableOverviewQuery.error
            ? String(tableOverviewQuery.error)
            : null,
          tableOverview: tableOverviewQuery.data,
        };
      }
      return {
        isLoading: dataSourceOverviewQuery.isLoading,
        error: dataSourceOverviewQuery.error
          ? String(dataSourceOverviewQuery.error)
          : null,
        overview: dataSourceOverviewQuery.data,
      };
    }
    case "tables": {
      if (isDataModel) {
        return {
          isLoading: dataModelTablesQuery.isLoading,
          error: dataModelTablesQuery.error
            ? String(dataModelTablesQuery.error)
            : null,
          tables: dataModelTablesQuery.data,
        };
      }
      return {
        isLoading: tablesQuery.isLoading,
        error: tablesQuery.error ? String(tablesQuery.error) : null,
        tables: tablesQuery.data,
      };
    }
    case "reports": {
      if (isOntology) {
        return {
          isLoading: ontologyReportsQuery.isLoading,
          error: ontologyReportsQuery.error
            ? String(ontologyReportsQuery.error)
            : null,
          ontologyReports: ontologyReportsQuery.data,
        };
      }
      return {
        isLoading: reportsQuery.isLoading,
        error: reportsQuery.error ? String(reportsQuery.error) : null,
        reports: reportsQuery.data,
      };
    }
    case "relationship": {
      return {
        isLoading: relationshipsQuery.isLoading,
        error: relationshipsQuery.error
          ? String(relationshipsQuery.error)
          : null,
        relationships: relationshipsQuery.data,
      };
    }
    // Data model tab id is plural ("relationships") — two-line pair rows.
    case "relationships": {
      return {
        isLoading: dataModelRelationshipsQuery.isLoading,
        error: dataModelRelationshipsQuery.error
          ? String(dataModelRelationshipsQuery.error)
          : null,
        relationshipPairs: dataModelRelationshipsQuery.data,
      };
    }
    case "links": {
      return {
        isLoading: ontologyLinksQuery.isLoading,
        error: ontologyLinksQuery.error
          ? String(ontologyLinksQuery.error)
          : null,
        links: ontologyLinksQuery.data,
      };
    }
    case "metrics": {
      const metricsQuery = isDataModel
        ? dataModelMetricsQuery
        : isOntology
          ? ontologyMetricsQuery
          : schemaMetricsQuery;
      return {
        isLoading: metricsQuery.isLoading,
        error: metricsQuery.error ? String(metricsQuery.error) : null,
        metrics: metricsQuery.data,
      };
    }
    case "rules": {
      return {
        isLoading: ontologyRulesQuery.isLoading,
        error: ontologyRulesQuery.error
          ? String(ontologyRulesQuery.error)
          : null,
        ruleSections: ontologyRulesQuery.data,
      };
    }
    case "formula": {
      return {
        isLoading: metricFormulaQuery.isLoading,
        error: metricFormulaQuery.error
          ? String(metricFormulaQuery.error)
          : null,
        metricFormula: metricFormulaQuery.data,
        metricOverview: metricOverviewQuery.data,
      };
    }
    case "trace": {
      if (isDataStory) {
        return {
          isLoading: dataStoryTraceQuery.isLoading,
          error: dataStoryTraceQuery.error
            ? String(dataStoryTraceQuery.error)
            : null,
          traceSections: dataStoryTraceQuery.data,
          dataStoryOverview: dataStoryOverviewQuery.data,
        };
      }
      return {
        isLoading: reportTraceQuery.isLoading,
        error: reportTraceQuery.error
          ? String(reportTraceQuery.error)
          : null,
        traceSections: reportTraceQuery.data,
        reportOverview: reportOverviewQuery.data,
      };
    }
    case "activity": {
      if (isArtifact) {
        return {
          isLoading: artifactActivityQuery.isLoading,
          error: artifactActivityQuery.error
            ? String(artifactActivityQuery.error)
            : null,
          artifactActivity: artifactActivityQuery.data,
          artifactOverview: artifactOverviewQuery.data,
        };
      }
      if (isDataStory) {
        return {
          isLoading: dataStoryActivityQuery.isLoading,
          error: dataStoryActivityQuery.error
            ? String(dataStoryActivityQuery.error)
            : null,
          dataStoryActivity: dataStoryActivityQuery.data,
          dataStoryOverview: dataStoryOverviewQuery.data,
        };
      }
      return {
        isLoading: reportActivityQuery.isLoading,
        error: reportActivityQuery.error
          ? String(reportActivityQuery.error)
          : null,
        reportActivity: reportActivityQuery.data,
        reportOverview: reportOverviewQuery.data,
      };
    }
    case "usedIn": {
      const usedInQuery = isReport
        ? reportUsedInQuery
        : isMetric
          ? metricUsedInQuery
          : isOntology
            ? ontologyUsedInQuery
            : isTable
              ? schemaUsedInQuery
              : dataSourceUsedInQuery;
      return {
        isLoading: usedInQuery.isLoading,
        error: usedInQuery.error ? String(usedInQuery.error) : null,
        usedInSections: usedInQuery.data,
        ...(isMetric ? { metricOverview: metricOverviewQuery.data } : {}),
        ...(isReport ? { reportOverview: reportOverviewQuery.data } : {}),
      };
    }
    default:
      return { isLoading: false, error: null };
  }
};
